import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService, { Product, PaginatedResponse } from '../services/api';

const ProductListPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchProducts = async (search?: string, page: number = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = { page, limit: 50 };
      if (search) {
        params.search = search;
      }
      
      const response: PaginatedResponse<Product> = await apiService.getProducts(params);
      setProducts(response.results);
      setPagination({
        count: response.count,
        next: response.next,
        previous: response.previous,
      });
    } catch (err) {
      setError('Failed to load products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(searchTerm, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProducts(searchTerm, 1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const formatPrice = (price: number | string | null) => {
    if (price === null || price === undefined) return 'N/A';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'N/A';
    return `${numPrice.toFixed(2)}€`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT');
  };

  const getPriceTypeColor = (type: string | null) => {
    switch (type) {
      case 'ZMSR': return 'primary';
      case 'ZPR1': return 'warning';
      case 'ZPM3': return 'success';
      default: return 'secondary';
    }
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-12">
          <h1>Lista de Produtos</h1>
          <p className="text-muted">
            Explore todos os produtos disponíveis e veja os preços atuais
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSearch}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por SKU..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-primary" type="submit">
                    Buscar
                  </button>
                  {searchTerm && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                        fetchProducts('', 1);
                      }}
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Results Info */}
      {pagination && (
        <div className="row mb-3">
          <div className="col-12">
            <p className="text-muted">
              {searchTerm ? `Resultados para "${searchTerm}": ` : ''}
              {pagination.count} produtos encontrados
            </p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Products Table */}
      {!loading && !error && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Preço Atual</th>
                        <th>Tipo Ativo</th>
                        <th>Última Atualização</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.sku} className="product-card">
                          <td>
                            <strong>{product.sku}</strong>
                          </td>
                          <td>
                            <span className="price-badge">
                              {formatPrice(product.current_price)}
                            </span>
                          </td>
                          <td>
                            {product.current_active_type && (
                              <span className={`badge bg-${getPriceTypeColor(product.current_active_type)}`}>
                                {product.current_active_type}
                              </span>
                            )}
                          </td>
                          <td className="text-muted">
                            {formatDate(product.last_date)}
                          </td>
                          <td>
                            <Link 
                              to={`/products/${product.sku}`} 
                              className="btn btn-sm btn-outline-primary"
                            >
                              Ver Histórico
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {products.length === 0 && !loading && (
                  <div className="text-center py-4">
                    <p className="text-muted">Nenhum produto encontrado.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.count > 50 && (
        <div className="row mt-4">
          <div className="col-12">
            <nav>
              <ul className="pagination justify-content-center">
                <li className={`page-item ${!pagination.previous ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.previous}
                  >
                    Anterior
                  </button>
                </li>
                
                <li className="page-item active">
                  <span className="page-link">
                    Página {currentPage}
                  </span>
                </li>
                
                <li className={`page-item ${!pagination.next ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.next}
                  >
                    Próxima
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductListPage;